import CopyLinkButton from '@/components/Discord/CopyLinkButton'
import { useState } from 'react'
import GuidelineTooltip from '../../../components/Discord/GuidelineTooltip'
import styles from '@/components/Discord/DiscordDocument.module.css'
import DiscordDocument from '@/components/Discord/DiscordDocument'

function DiscordSuicideSelfHarmPolicyPage() {
  const [language, setLanguage] = useState<'en' | 'cs'>('cs')
  const [encouragementExamplesOpen, setEncouragementExamplesOpen] = useState(false)

  return (
    <DiscordDocument
      language={language}
      onLanguageChange={setLanguage}
      heading={
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            {language === 'cs'
              ? 'Politika sebevraždy a sebepoškozování'
              : 'Suicide and Self-Harm Policy'}
          </h1>
        </div>
      }
      notice={
        <>
          <div className={styles.disclaimer}>
            <div className={styles.disclaimerCard} lang="cs">
              <h3>Důležité upozornění</h3>
              <p>
                Nejsme přidruženi ani spojeni s Discord Inc. Toto je pouze překlad oficiální
                politiky sebevraždy a sebepoškozování Discord dostupné na{' '}
                <a
                  href="https://discord.com/safety/suicide-self-harm-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  discord.com/safety/suicide-self-harm-policy-explainer
                </a>
                .
              </p>
              <p>
                Tento překlad nemusí být aktuální s oficiální politikou. Pokud si všimnete rozdílu,
                prosím dejte nám vědět na našem Discord serveru.
              </p>
            </div>
            <div className={styles.disclaimerCard} lang="en">
              <h3>Important Notice</h3>
              <p>
                We are not affiliated with Discord Inc. This is only a translation of the official
                Discord suicide and self-harm policy available at{' '}
                <a
                  href="https://discord.com/safety/suicide-self-harm-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  discord.com/safety/suicide-self-harm-policy-explainer
                </a>
                .
              </p>
              <p>
                This translation may not be up to date with the official policy. If you notice a
                difference, please let us know on our Discord server.
              </p>
            </div>
          </div>
        </>
      }
    >
      {/* Disclaimer section - always show both languages */}

      {/* Quick Summary - Czech only */}
      {language === 'cs' && (
        <details className={styles.quickSummary} id="quick-summary">
          <summary>
            <h2>Rychlé shrnutí politiky</h2>
          </summary>
          <CopyLinkButton
            sectionId={'quick-summary'}
            language={language}
            className={styles.copyBtn}
          />

          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <h3>Co je zakázáno</h3>
              <ul>
                <li>Koordinace sebepoškozování</li>
                <li>Zobrazování grafických obrázků sebepoškozování</li>
                <li>Sdílení metod nebo rad k sebepoškozování</li>
                <li>Normalizace nebo povzbuzování k sebepoškozování</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Naše závazky</h3>
              <ul>
                <li>Vytváříme bezpečné online prostředí</li>
                <li>Podporujeme duševní pohodu</li>
                <li>Chráníme před škodlivým obsahem</li>
                <li>Zajišťujeme, aby se každý cítil v bezpečí</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Pomoc a zdroje</h3>
              <ul>
                <li>Partnerství s Crisis Text Line</li>
                <li>Podpora 24/7 přes textové zprávy</li>
                <li>Kontaktujte záchranné složky v případě nebezpečí</li>
                <li>Další zdroje na stránkách Discord</li>
              </ul>
            </div>
          </div>
          <p className={styles.summaryNote}>
            Toto je pouze stručné shrnutí. Pro úplné znění čtěte celý dokument níže.
          </p>
        </details>
      )}

      {/* Warning Box - Czech only */}
      {language === 'cs' && (
        <div className={styles.warningBox}>
          <p>
            <strong>Varování:</strong> Tato stránka obsahuje citlivý obsah týkající se
            sebepoškozování a sebevražd. Pokud potřebujete pomoc, prosím kontaktujte místní krizovou
            linku nebo záchranné služby.
          </p>
        </div>
      )}

      {/* Policy Body */}
      <div className={styles.policyBody}>
        {language === 'cs' ? (
          <>
            <section id="suicide-self-harm-risk" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'suicide-self-harm-risk'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>Riziko sebevraždy a sebepoškozování</h2>
              <p>
                Na Discordu se zavazujeme vytvářet bezpečné online prostředí a předcházet škodám.
                Chápeme důležitost silné komunity a pozitivního prostředí, pokud jde o duševní
                pohodu.
              </p>
              <p>
                Nepovolujeme koordinaci sebepoškozování či sebevraždy ani jejich grafické
                zobrazování. Sebepoškozování je, když si někdo záměrně fyzicky ubližuje. Příklady
                chování sebepoškozování zahrnují úmyslné ukončení vlastního života, zapojení se do
                poruch příjmu potravy atd.
              </p>
            </section>

            <section id="encouragement-promotion" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'encouragement-promotion'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>Povzbuzování a propagace sebepoškozování</h2>
              <p>
                Používání Discordu k oslavování nebo propagaci sebevraždy nebo sebepoškozování není
                za žádných okolností povoleno. Věříme v podporu pozitivního a zdravého prostředí,
                kde se uživatelé mohou scházet a vzájemně se podporovat. Je však důležité, aby tyto
                diskuse nepropagovaly, neoslavovaly ani nenormalizovaly chování sebepoškozování.
                Naším cílem je zajistit, aby se každý cítil bezpečně a podporován.
              </p>
            </section>

            <section className={styles.examplesSection}>
              <button
                className={styles.dropdownToggle}
                onClick={() => setEncouragementExamplesOpen(!encouragementExamplesOpen)}
                aria-expanded={encouragementExamplesOpen}
                type="button"
              >
                <span>
                  Například nesmíte zveřejňovat, sdílet nebo se zapojovat do: [varování o citlivém
                  obsahu]
                </span>
                <span className={styles.dropdownIcon}>{encouragementExamplesOpen ? '−' : '+'}</span>
              </button>

              {encouragementExamplesOpen && (
                <div className={styles.dropdownContent}>
                  <ul>
                    <li>Nabízení nebo sdílení metod nebo rad, jak spáchat čin sebepoškozování</li>
                    <li>Normalizace nebo povzbuzování chování sebepoškozování</li>
                    <li>Odrazování jednotlivců od vyhledání léčby chování sebepoškozování</li>
                    <li>
                      Sdílení materiálu o sebepoškozování za účelem vyvolání reakce sebepoškozování
                      u někoho jiného
                    </li>
                  </ul>
                </div>
              )}
            </section>

            <section id="eating-disorders" className={styles.policySection}>
              <CopyLinkButton
                sectionId="eating-disorders"
                language={language}
                className={styles.copyBtn}
              />
              <h2>Poruchy příjmu potravy</h2>
              <p>
                Zakázána je propagace poruch příjmu potravy a povzbuzování k souvisejícímu chování,
                například nadměrnému hladovění nebo přejídání následovanému zvracením. Nesmíte
                sdílet obsah, strategie ani návody podporující takové jednání.
              </p>
            </section>
            <section id="how-to-avoid" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'how-to-avoid'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>Jak se vyhnout porušení těchto politik</h2>
              <p>
                Nahlaste a opusťte servery věnované výše popsanému obsahu porušujícímu pravidla.
                Discord může podniknout kroky i proti účtům přítomným na takových serverech.
              </p>
              <ul>
                <li>
                  Pokud si nejste jisti, zda nějaký obsah může porušit tyto zásady, nezveřejňujte
                  jej
                </li>
                <li>Nesdílejte ilustrace sebepoškozování</li>
                <li>Nepovzbuzujte, nepropagujte ani nenormalizujte sebepoškozování</li>
                <li>Neodrazujte ostatní od vyhledání léčby chování sebepoškozování</li>
              </ul>
            </section>

            <section id="how-to-report" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'how-to-report'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>Jak nahlásit porušení těchto politik</h2>
              <p>
                Nahlašování porušení bezpečnosti je kriticky důležité pro udržení bezpečnosti vás a
                širší komunity Discord. Všichni uživatelé Discordu mohou nahlásit porušení zásad v
                aplikaci podle pokynů{' '}
                <a
                  href="/discord/safety/reporting-abusive-behavior-to-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  zde
                </a>
                . Pro více informací o těchto politikách se prosím podívejte na naše{' '}
                <a href="/discord/guidelines">Pokyny komunity</a>{' '}
                <GuidelineTooltip guidelineNumber="11" language={language}>
                  <a href="/discord/guidelines#guideline-11" className={styles.guidelineLink}>
                    #11
                  </a>
                </GuidelineTooltip>
                .
              </p>
            </section>

            <section id="resources" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'resources'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>Zdroje</h2>
              <p>
                Při sebevražedných myšlenkách vyhledejte krizovou pomoc; při bezprostředním ohrožení
                kontaktujte záchranné složky. Mimo USA nabízí ThroughLine přehled bezplatné a
                důvěrné pomoci podle země a jazyka.
              </p>
              <ul>
                <li>
                  <a href="https://988lifeline.org/">988 Lifeline (USA)</a>
                </li>
                <li>
                  <a href="https://www.crisistextline.org/">Crisis Text Line (USA)</a>
                </li>
                <li>
                  <a href="https://www.nationaleatingdisorders.org/get-help/">NEDA</a>
                </li>
                <li>
                  <a href="https://anad.org/get-support/">ANAD</a>
                </li>
                <li>
                  <a href="https://discord.findahelpline.com/">ThroughLine — Find A Helpline</a>
                </li>
              </ul>
              <p className={styles.editorNote}>
                Poznámka Allcomu: jde o samostatné poskytovatele pomoci. Aktuální kontakty a
                dostupnost ověřte přímo na jejich stránkách; u ANAD odkazujeme na web příslušné
                organizace.
              </p>
              <p>
                <strong>
                  Pokud jste vy nebo jiný uživatel v bezprostředním nebezpečí, okamžitě kontaktujte
                  záchranné složky a dejte jim vědět, co se děje, bez ohledu na informace, které
                  můžete poskytnout.
                </strong>
              </p>
              <ul>
                <li>
                  Discord spolupracuje s{' '}
                  <a
                    href="https://www.crisistextline.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Crisis Text Line
                  </a>
                  , neziskovou organizací, která poskytuje 24/7 textovou podporu duševního zdraví a
                  krizovou intervenci prostřednictvím vyškolených dobrovolných krizových poradců.
                  Crisis Text Line je v současné době k dispozici těm ve Spojených státech a je
                  nabízena v angličtině i španělštině. Více o tomto partnerství si můžete přečíst{' '}
                  <a
                    href="https://discord.com/blog/an-update-on-our-mental-health-belonging-work"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    zde
                  </a>
                  .
                </li>
              </ul>
              <p>
                Další zdroje, jako jsou čísla krizových linek, najdete{' '}
                <a
                  href="https://discord.com/safety/360044103771-mental-health-on-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  zde
                </a>
                .
              </p>
            </section>
          </>
        ) : (
          <>
            <section id="suicide-self-harm-risk" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'suicide-self-harm-risk'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>Suicide and Self-Harm Risk</h2>
              <p>
                At Discord, we are committed to creating a safe online environment and preventing
                harm. We understand the importance of a strong community and positive surroundings
                when it comes to mental well-being.
              </p>
              <p>
                We do not allow coordinating suicide or self-harm, or graphically depicting either.
                Self-harm is when someone deliberately hurts themselves physically. Examples of
                self-harm behaviors include intentionally ending one's life, engaging in disordered
                eating patterns, etc.
              </p>
            </section>

            <section id="encouragement-promotion" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'encouragement-promotion'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>Self-Harm Encouragement and Promotion</h2>
              <p>
                Using Discord to glorify or promote suicide or self-harm is not allowed under any
                circumstance. We believe in fostering a positive and healthy environment where users
                can come together to support one another. However, it is important that these
                discussions do not promote, glorify or normalize self-harm behaviors. Our goal is to
                ensure that everyone feels safe and supported.
              </p>
            </section>

            <section className={styles.examplesSection}>
              <button
                className={styles.dropdownToggle}
                onClick={() => setEncouragementExamplesOpen(!encouragementExamplesOpen)}
                aria-expanded={encouragementExamplesOpen}
                type="button"
              >
                <span>
                  For example, you may not post, share, or engage in: [sensitive language content
                  warning]
                </span>
                <span className={styles.dropdownIcon}>{encouragementExamplesOpen ? '−' : '+'}</span>
              </button>

              {encouragementExamplesOpen && (
                <div className={styles.dropdownContent}>
                  <ul>
                    <li>
                      Offering or sharing methods or advice on how to commit an act of self-harm
                    </li>
                    <li>Normalizing or encouraging self-harming behaviors</li>
                    <li>
                      Discouraging individuals from seeking treatment for self-harming behaviors
                    </li>
                    <li>
                      Sharing self-harm material in order to trigger a self-harm response in someone
                      else
                    </li>
                  </ul>
                </div>
              )}
            </section>

            <section id="eating-disorders" className={styles.policySection}>
              <CopyLinkButton
                sectionId="eating-disorders"
                language={language}
                className={styles.copyBtn}
              />
              <h2>Eating disorders</h2>
              <p>
                Content or conduct encouraging eating disorders is prohibited, including excessive
                fasting or bingeing and purging. Do not promote these disorders or share strategies
                or instructions for engaging in such behaviour.
              </p>
            </section>
            <section id="how-to-avoid" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'how-to-avoid'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>How to Avoid Violating These Policies</h2>
              <p>
                Please report and leave servers dedicated to the violative content described above.
                Discord may also take action against accounts that are present in violative servers.
              </p>
              <ul>
                <li>
                  If you are not sure whether a piece of content might violate these policies, don't
                  post it
                </li>
                <li>Do not share illustrations of self-harm</li>
                <li>Do not encourage, promote, or normalize self-harm</li>
                <li>Do not discourage others from seeking treatment for self-harming behaviors</li>
              </ul>
            </section>

            <section id="how-to-report" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'how-to-report'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>How to Report Violations of These Policies</h2>
              <p>
                Reporting safety violations is critically important to keeping you and the broader
                Discord community safe. All Discord users can report policy violations in the app by
                following the instructions{' '}
                <a
                  href="/discord/safety/reporting-abusive-behavior-to-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                . For more information on these policies, please reference our{' '}
                <a href="https://discord.com/guidelines" target="_blank" rel="noopener noreferrer">
                  Community Guidelines
                </a>{' '}
                <GuidelineTooltip guidelineNumber="11" language={language}>
                  <a href="/discord/guidelines#guideline-11" className={styles.guidelineLink}>
                    #11
                  </a>
                </GuidelineTooltip>
                .
              </p>
            </section>

            <section id="resources" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'resources'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>Resources</h2>
              <p>
                Seek crisis support for suicidal thoughts and contact emergency services for
                immediate danger. Outside the US, ThroughLine lists free, confidential support by
                country and language.
              </p>
              <ul>
                <li>
                  <a href="https://988lifeline.org/">988 Lifeline (USA)</a>
                </li>
                <li>
                  <a href="https://www.crisistextline.org/">Crisis Text Line (USA)</a>
                </li>
                <li>
                  <a href="https://www.nationaleatingdisorders.org/get-help/">NEDA</a>
                </li>
                <li>
                  <a href="https://anad.org/get-support/">ANAD</a>
                </li>
                <li>
                  <a href="https://discord.findahelpline.com/">ThroughLine — Find A Helpline</a>
                </li>
              </ul>
              <p className={styles.editorNote}>
                Allcom note: these are independent support providers. Check their websites for
                current contact details and availability; the ANAD link points to its own
                organisation.
              </p>
              <p>
                <strong>
                  If you or another user is in immediate danger, please contact law enforcement
                  right away and let them know what's going on, regardless of the information you're
                  able to provide.
                </strong>
              </p>
              <ul>
                <li>
                  Discord partners with{' '}
                  <a
                    href="https://www.crisistextline.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Crisis Text Line
                  </a>
                  , a nonprofit that provides 24/7 text-based mental health support and crisis
                  intervention via trained volunteer crisis counselors. Crisis Text Line is
                  currently available to those in the United States and is offered in both English
                  and Spanish. You can read more about this partnership{' '}
                  <a
                    href="https://discord.com/blog/an-update-on-our-mental-health-belonging-work"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                  .
                </li>
              </ul>
              <p>
                Other resources, such as lifeline numbers, can be found{' '}
                <a
                  href="https://discord.com/safety/360044103771-mental-health-on-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>
            </section>
          </>
        )}
      </div>

      {/* Tags */}
      <div className={styles.tags}>
        <div className={styles.tagsLabel}>Tags</div>
        <div className={styles.tagsList}>
          <div className={styles.tag}>
            {language === 'cs' ? 'Bezpečnost uživatelů' : 'User Safety'}
          </div>
        </div>
      </div>
    </DiscordDocument>
  )
}

export default DiscordSuicideSelfHarmPolicyPage
